<template>
    <div class="vrm-form__fieldset-item vrm-file-picker">
        <label class="sg-textfield-label" :class="{ 'sg-required': required }">
            <span class="sg-textfield-label-caption">{{ label }}</span>
        </label>

        <input
            ref="input"
            type="file"
            class="sg-element"
            :required="required"
            :class="{ 'sg-error': hasError }"
            :accept="acceptedFileTypes"
            :multiple="multiple"
            @change="onFileChanged"
        />

        <div class="vrm-file-picker__field" @click="selectFile">
            <slot name="leading"></slot>

            <div class="vrm-file-picker__content">
                <span v-if="placeholder && !hasValue" class="vrm-file-picker__placeholder">
                    {{ placeholder }}
                </span>
                <span v-if="hasValue">{{ filename }}</span>
            </div>

            <div class="vrm-file-picker__action">
                <a>{{ actionText }}</a>
            </div>
        </div>

        <div v-if="hasError" class="vrm-form_input-errors">
            <span class="sg-input-error">{{ error }}</span>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';

@Component({ inheritAttrs: false })
export default class VrmFileUploadField extends Vue {
    /**
     * Form field label.
     *
     * If empty, no label will be shown.
     */
    @Prop({ type: String, default: undefined })
    label;

    /**
     * Array of allowed file types for the file picker.
     *
     * If empty, all file types will be accepted.
     */
    @Prop({ type: Array, default: undefined })
    acceptedFileTypes;

    /**
     * Error message to display for this form field.
     *
     * If left empty, no error message will be shown.
     */
    @Prop({ type: String, default: undefined })
    error;

    /**
     * The 'Upload file' action text to display in the form field.
     *
     * If left empty, defaults to "Upload file".
     */
    @Prop({ type: String, default: 'Upload file' })
    actionText;

    /**
     * Whether the field is required
     *
     * Defaults to false.
     */
    @Prop({ type: Boolean, default: false })
    required;

    /**
     * Whether the field allows you to select multiple files or just 1.
     *
     * Defaults to false.
     */
    @Prop({ type: Boolean, default: false })
    multiple;

    /**
     * Placeholder text to display before any file is selected.
     *
     * When a file is chosen, the placeholder will be replaced with the selected file name.
     */
    @Prop({ type: String, default: undefined })
    placeholder;

    /**
     * The file that is currently selected by the user.
     */
    value = null;

    /**
     * Check if an error message is set for this file picker.
     *
     * @returns {boolean}
     */
    get hasError() {
        return this.error && this.error.length > 0;
    }

    /**
     * Check if a value has been selected for this file picker.
     *
     * @returns {boolean}
     */
    get hasValue() {
        return this.value && this.value.length > 0;
    }

    /**
     * Returns the filename for the selected file.
     *
     * If multi select, the filenames of all selected files will be concatenated.
     *
     * @returns {*|string}
     */
    get filename() {
        if (this.value) {
            if (this.value.length === 1) {
                return this.value[0].name;
            } else {
                return this.value.map(e => e.name).join(', ');
            }
        }

        return '';
    }

    /**
     * Open the browsers file picker so the user can select one (or multiple) files.
     *
     * @param event
     */
    selectFile(event) {
        event.preventDefault();
        event.stopPropagation();

        this.$refs.input.click();
    }

    /**
     * Callback when a new file (or files) is selected.
     *
     * Emits a "change" event.
     */
    onFileChanged() {
        const files = this.$refs.input.files;

        // Empty FileList means the user has cancelled the file picker.
        // Don't update the components value to prevent overriding a selected value after cancelling.
        if (files && files.length > 0) {
            this.value = Array.from(files);
            this.$emit('change', this.value);
        }
    }
}
</script>
